import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { dashboardApi } from "../../api/index.js";
import AppSelect from "../../components/ui/AppSelect";

const ACTIVE_STATUSES = "QUOTE_PENDING,QUOTED,ACCEPTED,SCHEDULING,SCHEDULED,IN_TRANSIT,REJECTED,EXPIRED";
const statusVariant = {
  QUOTE_PENDING: "warning",
  QUOTED: "info",
  ACCEPTED: "success",
  REJECTED: "danger",
  SCHEDULED: "primary",
  IN_TRANSIT: "primary",
  EXPIRED: "secondary",
};
const money = (amount, currency = "UGX") =>
  `${currency} ${Number(amount || 0).toLocaleString()}`;
const dateTime = (value) => (value ? new Date(value).toLocaleString() : "—");
const orderName = (order) =>
  order.client_id?.name || order.customerName || order.shippingAddress?.customerName || "Customer";

function freightItemSummary(order) {
  const lines = order.freight?.assessment?.freightLines || [];
  if (lines.length) return lines.map((line) => `${line.quantity}× ${line.name}`).join(", ");
  return (order.orderItems || [])
    .slice(0, 3)
    .map((line) => `${line.qty || line.quantity || 1}× ${line.name || line.title || "Item"}`)
    .join(", ");
}

export default function FreightOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState(ACTIVE_STATUSES);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [quote, setQuote] = useState({ amount: "", paymentTerms: "PAY_ON_DELIVERY", notes: "", expiresAt: "" });
  const [schedule, setSchedule] = useState({ appointmentStart: "", appointmentEnd: "", carrierName: "", trackingId: "" });
  const [payment, setPayment] = useState({ paymentStatus: "PAID", paymentReference: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await dashboardApi.get("/weborders/freight/pending", {
        params: { status: filter, limit: 100 },
      });
      setOrders(data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.error?.message || "Could not load freight orders.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openQuote = (order) => {
    setSelected(order);
    setQuote({
      amount: order.freight?.quotedAmount ?? "",
      paymentTerms: order.freight?.paymentTerms || "PAY_ON_DELIVERY",
      notes: order.freight?.quoteNotes || "",
      expiresAt: "",
    });
    setModal("quote");
  };
  const openSchedule = (order) => {
    setSelected(order);
    setSchedule({ appointmentStart: "", appointmentEnd: "", carrierName: order.freight?.carrierName || "", trackingId: order.freight?.trackingId || "" });
    setModal("schedule");
  };
  const openPayment = (order) => {
    setSelected(order);
    setPayment({ paymentStatus: order.freight?.paymentStatus || "PAID", paymentReference: order.freight?.paymentReference || "" });
    setModal("payment");
  };

  const submit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      if (modal === "quote") {
        await dashboardApi.patch(`/weborders/${selected._id}/freight/quote`, {
          ...quote,
          amount: Number(quote.amount),
          expiresAt: quote.expiresAt ? new Date(quote.expiresAt).toISOString() : undefined,
        });
        toast.success("Freight quote sent to the customer.");
      } else if (modal === "schedule") {
        await dashboardApi.patch(`/weborders/${selected._id}/freight/schedule`, {
          ...schedule,
          appointmentStart: new Date(schedule.appointmentStart).toISOString(),
          appointmentEnd: schedule.appointmentEnd
            ? new Date(schedule.appointmentEnd).toISOString()
            : undefined,
        });
        toast.success("Freight appointment scheduled.");
      } else if (modal === "payment") {
        await dashboardApi.patch(`/weborders/${selected._id}/freight/payment`, payment);
        toast.success("Freight payment status updated.");
      }
      setModal(null);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || "Freight update failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content">
      <Row>
        <Col md={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-start flex-wrap">
              <div>
                <Card.Title as="h4">Arranged Freight</Card.Title>
                <p className="text-muted mb-0">
                  Quote transport separately, record customer approval, then arrange the carrier and appointment.
                </p>
              </div>
              <Button variant="outline-primary" onClick={load} disabled={loading}>Refresh</Button>
            </Card.Header>
            <Card.Body>
              <Alert variant="info">
                Merchandise checkout never includes these delivery charges. Carrier assignment waits for customer acceptance; movement waits for a scheduled appointment, a compatible vehicle, and any required freight prepayment.
              </Alert>
              <Form.Group style={{ maxWidth: 360 }} className="mb-3">
                <Form.Label>Status</Form.Label>
                <AppSelect block value={filter} onChange={(event) => setFilter(event.target.value)}>
                  <option value={ACTIVE_STATUSES}>All active freight</option>
                  <option value="QUOTE_PENDING">Needs quote</option>
                  <option value="QUOTED">Awaiting customer</option>
                  <option value="ACCEPTED">Accepted — arrange delivery</option>
                  <option value="SCHEDULED">Scheduled</option>
                  <option value="IN_TRANSIT">In transit</option>
                  <option value="REJECTED,EXPIRED">Rejected or expired</option>
                </AppSelect>
              </Form.Group>

              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
              ) : orders.length === 0 ? (
                <Alert variant="light">No freight orders match this filter.</Alert>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr><th>Order</th><th>Customer / load</th><th>Status</th><th>Quote</th><th>Appointment</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order._id}>
                        <td>
                          <strong>#{String(order._id).slice(-8).toUpperCase()}</strong>
                          <div className="small text-muted">{dateTime(order.createdAt)}</div>
                        </td>
                        <td style={{ minWidth: 260 }}>
                          <strong>{orderName(order)}</strong>
                          <div className="small">{freightItemSummary(order) || "Freight-classified order"}</div>
                          <div className="small text-muted">Reasons: {(order.freight?.assessment?.reasons || []).join(", ") || "product rule"}</div>
                          <div className="small text-muted">Minimum vehicle: {order.freight?.assessment?.requiredCarrierType || "ANY"}</div>
                        </td>
                        <td>
                          <Badge bg={statusVariant[order.freight?.status] || "secondary"}>{order.freight?.status}</Badge>
                          <div className="small mt-1">Payment: {order.freight?.paymentStatus}</div>
                        </td>
                        <td>
                          {order.freight?.quotedAt
                            ? <><strong>{money(order.freight.quotedAmount, order.freight.currency)}</strong><div className="small text-muted">expires {dateTime(order.freight.quoteExpiresAt)}</div></>
                            : "Not quoted"}
                        </td>
                        <td>
                          {order.freight?.appointmentStart ? <><strong>{dateTime(order.freight.appointmentStart)}</strong><div className="small text-muted">{order.freight.carrierName || "Carrier pending"}</div></> : "—"}
                        </td>
                        <td style={{ minWidth: 220 }}>
                          <Button size="sm" variant="outline-primary" className="mr-1 mb-1" onClick={() => openQuote(order)}>Quote</Button>{" "}
                          {['ACCEPTED', 'SCHEDULING', 'SCHEDULED'].includes(order.freight?.status) && (
                            <Button size="sm" variant="outline-success" className="mr-1 mb-1" onClick={() => openSchedule(order)}>Schedule</Button>
                          )}{" "}
                          {order.freight?.quotedAt && (
                            <Button size="sm" variant="outline-secondary" className="mb-1" onClick={() => openPayment(order)}>Payment</Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={Boolean(modal)} onHide={() => !saving && setModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{modal === "quote" ? "Send freight quote" : modal === "schedule" ? "Schedule freight" : "Update freight payment"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modal === "quote" && (
            <>
              <Form.Group className="mb-3"><Form.Label>Freight amount</Form.Label><Form.Control type="number" min="0" value={quote.amount} onChange={(e) => setQuote({ ...quote, amount: e.target.value })} /></Form.Group>
              <Form.Group className="mb-3"><Form.Label>Payment terms</Form.Label><AppSelect block value={quote.paymentTerms} onChange={(e) => setQuote({ ...quote, paymentTerms: e.target.value })}><option value="PAY_ON_DELIVERY">Pay carrier / freight on delivery</option><option value="PAY_BEFORE_DELIVERY">Pay before dispatch</option></AppSelect></Form.Group>
              <Form.Group className="mb-3"><Form.Label>Quote expiry (optional)</Form.Label><Form.Control type="datetime-local" value={quote.expiresAt} onChange={(e) => setQuote({ ...quote, expiresAt: e.target.value })} /></Form.Group>
              <Form.Group><Form.Label>Customer notes</Form.Label><Form.Control as="textarea" rows={3} value={quote.notes} onChange={(e) => setQuote({ ...quote, notes: e.target.value })} /></Form.Group>
            </>
          )}
          {modal === "schedule" && (
            <>
              <Form.Group className="mb-3"><Form.Label>Appointment start</Form.Label><Form.Control type="datetime-local" value={schedule.appointmentStart} onChange={(e) => setSchedule({ ...schedule, appointmentStart: e.target.value })} /></Form.Group>
              <Form.Group className="mb-3"><Form.Label>Appointment end</Form.Label><Form.Control type="datetime-local" value={schedule.appointmentEnd} onChange={(e) => setSchedule({ ...schedule, appointmentEnd: e.target.value })} /></Form.Group>
              <Form.Group className="mb-3"><Form.Label>Carrier</Form.Label><Form.Control required value={schedule.carrierName} onChange={(e) => setSchedule({ ...schedule, carrierName: e.target.value })} /></Form.Group>
              <Form.Group><Form.Label>Tracking / consignment ID</Form.Label><Form.Control value={schedule.trackingId} onChange={(e) => setSchedule({ ...schedule, trackingId: e.target.value })} /></Form.Group>
            </>
          )}
          {modal === "payment" && (
            <>
              <Form.Group className="mb-3"><Form.Label>Payment status</Form.Label><AppSelect block value={payment.paymentStatus} onChange={(e) => setPayment({ ...payment, paymentStatus: e.target.value })}><option value="PENDING">Pending</option><option value="PAID">Paid</option><option value="COD">Pay on delivery</option><option value="WAIVED">Waived</option></AppSelect></Form.Group>
              <Form.Group><Form.Label>Payment reference</Form.Label><Form.Control value={payment.paymentReference} onChange={(e) => setPayment({ ...payment, paymentReference: e.target.value })} /></Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setModal(null)} disabled={saving}>Cancel</Button><Button variant="primary" onClick={submit} disabled={saving || (modal === "quote" && quote.amount === "") || (modal === "schedule" && (!schedule.appointmentStart || !schedule.carrierName.trim()))}>{saving ? <Spinner size="sm" animation="border" /> : "Save"}</Button></Modal.Footer>
      </Modal>
    </div>
  );
}
